import { readFile, writeFile, access } from 'node:fs/promises';
import { join } from 'node:path';

/**
 * Required .gitignore patterns for Bluera Knowledge
 *
 * These patterns ensure:
 * - The .bluera/ data directory (vector DB, cloned repos) is ignored
 * - The .bluera/ logs directory is ignored
 * - Config files are NOT ignored (can be committed for team sharing):
 *   - stores.config.json (store definitions)
 *   - config.json (app configuration)
 *   - skill-activation.json (skill activation preferences)
 *
 * IMPORTANT: Git ignores children of ignored directories. To un-ignore a nested
 * file, you must first un-ignore each parent directory in the path. The order is:
 * 1. Ignore .bluera/ (everything ignored by default)
 * 2. Un-ignore .bluera/ itself (allow traversing into it)
 * 3. Un-ignore .bluera/bluera-knowledge/ (allow traversing deeper)
 * 4. Un-ignore the specific files we want tracked
 * 5. Re-ignore .bluera/bluera-knowledge/data/ and logs/ (keep untracked)
 */
const REQUIRED_PATTERNS = [
  '.bluera/',
  '!.bluera/',
  '!.bluera/bluera-knowledge/',
  '!.bluera/bluera-knowledge/stores.config.json',
  '!.bluera/bluera-knowledge/config.json',
  '!.bluera/bluera-knowledge/skill-activation.json',
  '.bluera/bluera-knowledge/data/',
  '.bluera/bluera-knowledge/logs/',
];

/**
 * Check if a required pattern is functionally covered by existing gitignore lines.
 *
 * Handles:
 * - Exact matches
 * - Trailing slash equivalence: `!dir` ↔ `!dir/` (both unignore the directory)
 * - Glob coverage: `.bluera/*` covers `.bluera/` (both ignore .bluera contents)
 * - When `.bluera/*` is present, `!.bluera/` is unnecessary (glob allows unignoring children)
 */
function isPatternCovered(pattern: string, existingLines: string[]): boolean {
  if (existingLines.includes(pattern)) return true;

  // Trailing slash equivalence for directory patterns
  // In .gitignore, `!dir` and `!dir/` are functionally equivalent for directories
  if (pattern.endsWith('/')) {
    const withoutSlash = pattern.slice(0, -1);
    if (existingLines.includes(withoutSlash)) return true;
  } else {
    if (existingLines.includes(`${pattern}/`)) return true;
  }

  // .bluera/* (glob) covers .bluera/ (directory ignore) — both ignore .bluera contents.
  // The glob form is actually preferable: it allows child unignore rules without
  // needing `!.bluera/` to re-enable traversal.
  if (pattern === '.bluera/') {
    if (existingLines.includes('.bluera/*')) return true;
  }

  // !.bluera/ is only needed to counteract `.bluera/` (directory ignore).
  // When `.bluera/*` is used instead, traversal into .bluera/ is already allowed,
  // so `!.bluera/` is unnecessary.
  if (pattern === '!.bluera/') {
    if (existingLines.includes('.bluera/*')) return true;
  }

  return false;
}

/**
 * Header comment for the gitignore section
 */
const SECTION_HEADER = `
# Bluera Knowledge
# Config files (stores.config.json, config.json, skill-activation.json) can be committed
# Data directory (vector DB, cloned repos) and logs are not committed
`;

/**
 * Check if a file exists
 */
async function fileExists(path: string): Promise<boolean> {
  try {
    await access(path);
    return true;
  } catch {
    return false;
  }
}

/**
 * Service for managing .gitignore patterns for Bluera Knowledge.
 *
 * When stores are created, this service ensures the project's .gitignore
 * is updated to:
 * - Ignore the .bluera/ data directory (not committed)
 * - Allow committing .bluera/bluera-knowledge/stores.config.json (for team sharing)
 */
export class GitignoreService {
  private readonly gitignorePath: string;

  constructor(projectRoot: string) {
    this.gitignorePath = join(projectRoot, '.gitignore');
  }

  /**
   * Check if all required patterns are present in .gitignore
   */
  async hasRequiredPatterns(): Promise<boolean> {
    const exists = await fileExists(this.gitignorePath);
    if (!exists) {
      return false;
    }

    const content = await readFile(this.gitignorePath, 'utf-8');
    const lines = content.split('\n').map((l) => l.trim());

    for (const pattern of REQUIRED_PATTERNS) {
      if (!isPatternCovered(pattern, lines)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Ensure required .gitignore patterns are present.
   *
   * - Creates .gitignore if it doesn't exist
   * - Appends missing patterns if .gitignore exists
   * - Does nothing if all patterns are already present
   *
   * @returns Object with updated flag and descriptive message
   */
  async ensureGitignorePatterns(): Promise<{ updated: boolean; message: string }> {
    const exists = await fileExists(this.gitignorePath);

    if (!exists) {
      // Create new .gitignore with our patterns
      const content = `${SECTION_HEADER.trim()}\n${REQUIRED_PATTERNS.join('\n')}\n`;
      await writeFile(this.gitignorePath, content);
      return {
        updated: true,
        message: 'Created .gitignore with Bluera Knowledge patterns',
      };
    }

    // Read existing content
    const existingContent = await readFile(this.gitignorePath, 'utf-8');
    const lines = existingContent.split('\n').map((l) => l.trim());

    // Find missing patterns
    const missingPatterns = REQUIRED_PATTERNS.filter(
      (pattern) => !isPatternCovered(pattern, lines)
    );

    if (missingPatterns.length === 0) {
      return {
        updated: false,
        message: 'All Bluera Knowledge patterns already present in .gitignore',
      };
    }

    // Append missing patterns
    let newContent = existingContent;
    if (!newContent.endsWith('\n')) {
      newContent += '\n';
    }

    newContent += SECTION_HEADER;
    newContent += `${missingPatterns.join('\n')}\n`;

    await writeFile(this.gitignorePath, newContent);

    return {
      updated: true,
      message: `Updated .gitignore with ${String(missingPatterns.length)} Bluera Knowledge pattern(s)`,
    };
  }

  /**
   * Get the path to the .gitignore file
   */
  getGitignorePath(): string {
    return this.gitignorePath;
  }
}
